import BaseAPI from './BaseAPI';
import { resolveEnv } from './idpEnv';

export const AGENT_NAMES = {
  CLINICAL_WORKFLOW_CHAT: 'clinical_workflow_chat',
} as const;

export type AgentName = (typeof AGENT_NAMES)[keyof typeof AGENT_NAMES] | string;

const AGENT_BASE_URLS: Record<string, string> = {
  production: 'https://agent.tigerconnect.com',
  uat: 'https://agent.uat.tigerconnect.com',
  env7: 'https://agent.env7.tigerconnect.com',
};

// Scopes the clinical-workflow agent requires on its bearer token.
const AGENT_SCOPES = 'agent:invoke openid profile email api.internal.basic messaging roster';

export type SSEEvent =
  | { event: 'message_start'; data: { id: string; type: string } }
  | { event: 'token'; data: { text: string } }
  | { event: 'message'; data: { text: string } }
  | { event: 'tool_call'; data: { id: string; name: string; args: Record<string, unknown> } }
  | { event: 'tool_result'; data: { tool_call_id: string; name: string; content: string } }
  | { event: 'message_end'; data: { id: string; type: string } }
  | { event: 'done'; data: { messages: unknown[]; session_id: string } }
  | { event: 'error'; data: { error: string; error_type: string } };

export type InvokeAgentOptions = {
  agentName?: AgentName;
  messages: Array<{ role: string; content: string }>;
  stream?: boolean;
  sessionId?: string;
  organizationId?: string;
  signal?: AbortSignal;
};

export default class AgentCoreAPI extends BaseAPI {
  /**
   * Get the agent invocation URL for the current environment.
   * Format: https://agent.{env}.tigerconnect.com/v1/agents/{agent_name}/invocations
   */
  getInvocationUrl(agentName: AgentName = AGENT_NAMES.CLINICAL_WORKFLOW_CHAT): string {
    const env = resolveEnv(this.config.baseUrl, this.config.apiEnv);
    const baseUrl = AGENT_BASE_URLS[env] || `https://agent.${env}.tigerconnect.com`;
    return `${baseUrl}/v1/agents/${agentName}/invocations`;
  }

  /**
   * Exchange API key/secret for an OAuth JWT (agent scopes) via the shared IDP
   * token provider. Caching, env resolution and sign-out eviction live there.
   */
  async getOAuthToken(organizationId: string): Promise<string> {
    return this.host.idpTokens.getToken({ organizationId, scope: AGENT_SCOPES });
  }

  /** Evict cached agent tokens (delegates to the shared provider). */
  clearTokenCache(organizationId?: string): void {
    this.host.idpTokens.clear(organizationId);
  }

  /** Invoked via AgentCoreService on client dispose/reset. */
  dispose(): void {
    this.host.idpTokens.clear();
  }

  /**
   * Invoke an AgentCore agent and stream SSE events.
   *
   * @param options - Invocation options
   * @param onEvent - Callback for each SSE event
   */
  async invoke(options: InvokeAgentOptions, onEvent: (event: SSEEvent) => void): Promise<void> {
    const { agentName, messages, stream = true, sessionId, organizationId, signal } = options;

    if (!organizationId) {
      throw new Error('organizationId is required for agent invocation');
    }

    const doFetch = async (tok: string) => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tok}`,
      };
      if (sessionId) {
        headers['X-Amzn-Bedrock-AgentCore-Runtime-Session-Id'] = sessionId;
      }
      return fetch(this.getInvocationUrl(agentName), {
        method: 'POST',
        headers,
        body: JSON.stringify({ messages, stream, session_id: sessionId }),
        signal,
      });
    };

    let token = await this.getOAuthToken(organizationId);
    let response = await doFetch(token);

    // On 401 the server may have rejected a near-expiry token the cache let through.
    // Evict the stale entry and retry once with a fresh token.
    if (response.status === 401) {
      this.clearTokenCache(organizationId);
      token = await this.getOAuthToken(organizationId);
      response = await doFetch(token);
    }

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`Agent invocation failed (${response.status}): ${errorBody}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith('data: ')) continue;

          let eventData: SSEEvent;
          try {
            eventData = JSON.parse(trimmed.slice(6)) as SSEEvent;
          } catch {
            // skip malformed SSE lines
            continue;
          }
          onEvent(eventData);
        }
      }

      // Flush any remaining data in the buffer if the stream ended without a trailing newline
      const remaining = buffer.trim();
      if (remaining && remaining.startsWith('data: ')) {
        let eventData: SSEEvent;
        try {
          eventData = JSON.parse(remaining.slice(6)) as SSEEvent;
        } catch {
          // skip malformed SSE lines
          return;
        }
        onEvent(eventData);
      }
    } finally {
      reader.releaseLock();
    }
  }
}
